import _ from 'lodash';

export function toLocalCycleBound (moment, utcValue) {
  return moment.utc(utcValue).local().format('YYYY-MM-DD HH:mm:ss');
}

export function buildCycleDefs (moment, rows) {
  return _.chain(rows)
    .filter(r => r.dt_inicio_quinzena != null)
    .uniqBy(r => `${r.dt_inicio_quinzena}|${r.dt_fim_quinzena}`)
    .map(r => {
      const start = toLocalCycleBound(moment, r.dt_inicio_quinzena);
      return {
        key: moment(start, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM'),
        start,
        end: toLocalCycleBound(moment, r.dt_fim_quinzena),
      };
    })
    .sortBy('start')
    .value();
}

export function hourSlotAt (moment, row) {
  const hora = String(row.hora).padStart(2, '0');
  return moment(`${row.dia} ${hora}:00:00`, 'YYYY-MM-DD HH:mm:ss');
}

export function resolveCycleKey (moment, cycleDefs, row) {
  const slot = hourSlotAt(moment, row);
  const cycle = _.find(cycleDefs, def => {
    const start = moment(def.start, 'YYYY-MM-DD HH:mm:ss');
    const end = moment(def.end, 'YYYY-MM-DD HH:mm:ss');
    return slot.isSameOrAfter(start) && slot.isSameOrBefore(end);
  });
  return cycle?.key ?? null;
}

function normalizeHourRow (row) {
  return {
    dia: row.dia,
    hora: row.hora,
    validas: _.isNil(row.horas_validas) ? 0 : row.horas_validas,
    invalidas: _.isNil(row.horas_invalidas) ? 0 : row.horas_invalidas,
    compensadas: _.isNil(row.horas_compensadas) ? 0 : row.horas_compensadas,
  };
}

function mergeHourRows (rows) {
  return _.map(_.groupBy(rows, r => `${r.dia}|${r.hora}`), group => ({
    dia: group[0].dia,
    hora: group[0].hora,
    validas: _.sumBy(group, 'validas'),
    invalidas: _.sumBy(group, 'invalidas'),
    compensadas: _.sumBy(group, 'compensadas'),
  }));
}

export function parseWorkhourCycles (moment, rows) {
  if (_.isEmpty(rows) || _.isEmpty(rows[0])) return {};

  const cycleDefs = buildCycleDefs(moment, rows);
  const grouped = _.groupBy(rows, row => resolveCycleKey(moment, cycleDefs, row));
  delete grouped[null];

  return _.mapValues(grouped, (ciclo, key) => {
    const def = _.find(cycleDefs, ['key', key]);
    const horas_diarias = mergeHourRows(_.map(ciclo, normalizeHourRow));

    const hours = _.reduce(horas_diarias, (calc, h) => ({
      valid: calc.valid + h.validas,
      invalid: calc.invalid + Math.abs(h.invalidas),
      compensated: calc.compensated + h.compensadas,
    }), { valid: 0, invalid: 0, compensated: 0 });

    return {
      start: def.start,
      end: def.end,
      valid: _.round(hours.valid, 1),
      invalid: _.round(hours.invalid, 1),
      compensated: _.round(hours.compensated, 1),
      daily: _.map(_.groupBy(horas_diarias, 'dia'), (d) => ({
        day: d[0].dia,
        valid: _.sumBy(d, 'validas'),
        invalid: Math.abs(_.sumBy(d, 'invalidas')),
        compensated: _.sumBy(d, 'compensadas'),
      })),
    };
  });
}
